import { createSelector } from 'reselect'

import { arrayOfObjectsToObject, arrayOfObjectsToObjectsOfArraysByKey } from 'utils/helpers'

import { selectActualCompanyName } from 'containers/App/selectors'
import { LEVEL_VALUES } from 'containers/AwarenessCenter/constants'

import { SENDING_VALUES } from '../constants'
import {
  selectParticipants,
  selectSelectedDelivery,
  selectSelectedScenarios,
  selectSendingType,
} from '../selectors'

import { CREATED_BY_OPTIONS, DEFAULT_CATEGORY_ID } from './constants'

export const selectError = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.error
export const selectScenariosLoader = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.scenariosLoader
export const selectSearch = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.search
export const selectSortOption = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.sortOption
export const selectLevelFilter = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.levelFilter
export const selectTypeFilter = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.typeFilter
export const selectCreatedByFilter = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.createdByFilter
export const selectLocales = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.locales
export const selectIsVisited = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.isVisited
export const selectCreatedByOptions = (state) => {
  const companyName = selectActualCompanyName(state)
  return [...CREATED_BY_OPTIONS, { value: 4, label: companyName }]
}
// Two Sending Type allowed (random, level)
export const selectSelectedScenariosByLevels = createSelector(
  selectSendingType,
  selectSelectedScenarios,
  selectParticipants,
  (sendingType, selectedScenarios, participants) => {
    if (sendingType === SENDING_VALUES.RANDOMIZED) {
      return [
        {
          level: LEVEL_VALUES.ALL_LEVELS,
          scenarios: selectedScenarios,
          participants: participants.meta.count,
        },
      ]
    } else if (sendingType === SENDING_VALUES.LEVELED) {
      const scenariosByLevels = arrayOfObjectsToObjectsOfArraysByKey(selectedScenarios, 'level')

      const allParticipatedUniqueLevels = [
        ...new Set([
          ...Object.keys(scenariosByLevels),
          ...Object.keys(participants.meta.awarenessLevels).filter(
            (level) => participants.meta.awarenessLevels[level]
          ),
        ]),
      ]

      return allParticipatedUniqueLevels.sort().map((level) => ({
        level: Number(level),
        scenarios: scenariosByLevels[level] || [],
        participants: participants.meta.awarenessLevels[level] || 0,
      }))
    } else throw new Error('2')
  }
)
export const selectSelectedScenariosMap = createSelector(
  selectSelectedScenarios,
  (selectedScenarios) => arrayOfObjectsToObject(selectedScenarios, 'id', true)
)
export const selectSelectedScenariosCount = (state) => selectSelectedScenarios(state).length
/**
 * Returning only existing participants levels
 * in following format: { [LEVEL_VALUES.MID_LEVEL]: bool, ... }
 */
export const selectIsLevelsFullyFilled = createSelector(
  selectParticipants,
  selectSelectedScenariosByLevels,
  (participants, selectedScenariosByLevelsArray) => {
    const selectedScenariosByLevels = arrayOfObjectsToObject(
      selectedScenariosByLevelsArray,
      'level'
    )

    return Object.keys(participants.meta.awarenessLevels)
      .filter((level) => participants.meta.awarenessLevels[level])
      .reduce(
        (res, level) => ({
          ...res,
          [level]: selectedScenariosByLevels[level]
            ? selectedScenariosByLevels[level].scenarios.length >=
              selectedScenariosByLevels[level].participants
            : false,
        }),
        {}
      )
  }
)
export const selectPickedCategoryId = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.pickedCategoryId
const selectCategories = (state) =>
  state.awarenessCenter.wizardSimulationCampaign.scenariosStep.data.categories
export const selectSortedCategories = createSelector(
  selectCategories,
  selectSelectedDelivery,
  (categories, delivery) =>
    Object.keys(categories)
      .map((categoryId) => ({
        id: categories[categoryId].id,
        name: categories[categoryId].name,
        totalCount: categories[categoryId].count,
        currentCount: categories[categoryId]
          ? categories[categoryId].count
          : categories[categoryId].count,
      }))
      .sort((a, b) => {
        if (a.id === DEFAULT_CATEGORY_ID[delivery]) return -1
        else if (b.id === DEFAULT_CATEGORY_ID[delivery]) return 1
        else return b.count - a.count
      })
)
export const selectScenarios = createSelector(
  selectCategories,
  selectSendingType,
  selectParticipants,
  selectPickedCategoryId,
  selectIsLevelsFullyFilled,
  selectSelectedScenariosCount,
  (
    categories,
    sendingType,
    participants,
    pickedCategoryId,
    isLevelsFullyFilled,
    selectedScenariosCount
  ) => {
    const pickedCategory = categories[pickedCategoryId]
    const relevantScenarios = pickedCategory?.templates || []
    const isRandomFullyFilled = selectedScenariosCount >= participants.meta.count

    return relevantScenarios.map((s) => {
      let selectionDisabled = false
      let selectionPopupText = null

      if (!participants.meta.count) {
        selectionDisabled = true
        selectionPopupText = null
      } else if (sendingType === SENDING_VALUES.LEVELED) {
        if (isLevelsFullyFilled[s.level] !== undefined) {
          selectionDisabled = isLevelsFullyFilled[s.level]
          selectionPopupText = selectionDisabled
            ? 'You cannot select more Scenarios on this level'
            : null
        } else {
          selectionDisabled = true
          selectionPopupText = 'There are no Participants on this level'
        }
      } else if (sendingType === SENDING_VALUES.RANDOMIZED) {
        selectionDisabled = isRandomFullyFilled
        selectionPopupText = selectionDisabled
          ? 'You cannot select more Scenarios than Participants'
          : null
      } else throw new Error('sendingType is not supported')

      return { ...s, selectionDisabled, selectionPopupText }
    })
  }
)
